/**
 * Alerts API client — talks to mttr/app_api.
 *
 * The Alerts page is platform-only: the backend gates every endpoint here on
 * the `dashboard.tag_health` capability (a token lacking it gets 403). The
 * token is sent via the `access-token` header (gen5-pwa convention).
 */

import { apiFetch } from "./apiClient";
import type { NeedsAttentionTag } from "./inventoryApi";

/** One fleet-wide unhealthy tag: a per-box NeedsAttentionTag plus the box it
 *  sits in, so the Alerts tab can group by smartbox. */
export interface UnhealthyTag extends NeedsAttentionTag {
  scu_name: string;
  /** Friendly SB###/ST### code; null when the box has no resolved code. */
  smartbox_code: string | null;
}

export interface UnhealthyTagsResponse {
  count: number;
  /** Echoed-back window (days) or null for all-time. */
  window_days: number | null;
  tags: UnhealthyTag[];
}

/** Fleet-wide FLICKERY/UNRELIABLE tags across the caller's visible boxes.
 *  `days` (1..365) narrows to recently-updated tags; omit for all-time. */
export function fetchUnhealthyTags(
  token: string,
  days: number | null = null,
): Promise<UnhealthyTagsResponse> {
  const qs = days == null ? "" : `?days=${encodeURIComponent(days)}`;
  return apiFetch<UnhealthyTagsResponse>(`/api/alerts/unhealthy-tags${qs}`, token);
}

/** One EPC with no product association, plus WHY it has none.
 *  `no_mapping`  — the tag is in no mapping table at all (the normal case:
 *                  nobody has matched it yet).
 *  `item_missing` — a mapping exists but points at a deleted product, so the
 *                  fix is to repoint/restore the product, not to match the tag.
 */
export interface UnmatchedEpc {
  epc: string;
  reason: "no_mapping" | "item_missing";
}

/** Unmatched tags for one smartbox, from its most recent scan. */
export interface UnmatchedTagBox {
  scu_name: string;
  /** Friendly SB###/ST### code; null when the box has no resolved code. */
  smartbox_code: string | null;
  /** When that scan finished (ISO, UTC). */
  scan_end_utc: string | null;
  /** Tags the scan read in total — the denominator for the unmatched count. */
  total_tags: number | null;
  /** Exact count, even when `epcs` below is truncated. */
  unmatched_count: number;
  epcs: UnmatchedEpc[];
  epcs_truncated: boolean;
}

export interface UnmatchedTagsResponse {
  box_count: number;
  total_unmatched: number;
  /** Echoed-back scan-freshness window (days), or null for all-time. */
  window_days: number | null;
  boxes: UnmatchedTagBox[];
}

/** EPCs present in each visible box's latest scan that resolve to no product.
 *  `days` (1..365) keeps only boxes SCANNED that recently — a box whose newest
 *  scan is months old is stale inventory, not a live alert. */
export function fetchUnmatchedTags(
  token: string,
  days: number | null = null,
): Promise<UnmatchedTagsResponse> {
  const qs = days == null ? "" : `?days=${encodeURIComponent(days)}`;
  return apiFetch<UnmatchedTagsResponse>(`/api/alerts/unmatched-tags${qs}`, token);
}
